<template>
    <section id="pricing" class="py-20 bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="text-center max-w-3xl mx-auto mb-16">
                <div class="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full mb-6 uppercase text-sm tracking-wider font-medium">
                    {{ $t('pricing.badge') }}
                </div>
                <h2 class="text-3xl sm:text-4xl mb-4">
                    {{ $t('pricing.title') }}
                </h2>
                <p class="text-lg text-muted-foreground">
                    {{ $t('pricing.subtitle') }}
                </p>
            </div>

            <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-8">
                <!-- Weekly Plan -->
                <div class="bg-white rounded-xl p-8 border border-border hover:shadow-lg transition-all flex flex-col">
                    <h3 class="text-xl mb-6 text-center">{{ $t('pricing.weekly.title') }}</h3>
                    <div class="text-center mb-8 flex-1 flex flex-col justify-center">
                        <div class="mb-3">
                            <div class="flex items-start justify-center gap-1">
                                <span class="text-5xl font-bold tracking-tight">{{ $t('pricing.weekly.price') }}</span>
                                <span class="text-5xl font-bold tracking-tight"> {{ $t('pricing.weekly.currency') }}</span>
                                <span class="text-lg text-muted-foreground">*</span>
                            </div>
                        </div>
                        <p class="text-xs text-muted-foreground uppercase tracking-wider">{{ $t('pricing.weekly.period') }}</p>
                        <p class="text-xs text-muted-foreground mt-1">{{ $t('pricing.vatNote') }}</p>
                    </div>

                    <div class="space-y-3 mb-8 mt-auto">
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.weekly.frequency') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.setup') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.monitoring') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.support') }}</span>
                        </div>
                    </div>

                    <a
                        href="#contact"
                        class="block w-full text-center border-2 border-border bg-white px-6 py-3 rounded-lg hover:bg-muted transition-all uppercase text-sm tracking-wide font-medium"
                    >
                        {{ $t('pricing.weekly.cta') }}
                    </a>
                </div>

                <!-- Daily Plan - Most Popular -->
                <div class="bg-gradient-to-b from-primary/5 to-white rounded-xl p-8 border-2 border-primary shadow-lg relative flex flex-col">
                    <div class="absolute -top-4 left-1/2 -translate-x-1/2">
                        <span class="bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold shadow-md">
                            {{ $t('pricing.daily.badge') }}
                        </span>
                    </div>

                    <h3 class="text-xl mb-6 text-center text-primary font-semibold">{{ $t('pricing.daily.title') }}</h3>
                    <div class="text-center mb-8 flex-1 flex flex-col justify-center">
                        <div class="mb-3">
                            <div class="flex items-start justify-center gap-1">
                                <span class="text-5xl font-bold tracking-tight text-primary">{{ $t('pricing.daily.price') }}</span>
                                <span class="text-5xl font-bold tracking-tight text-primary"> {{ $t('pricing.daily.currency') }}</span>
                                <span class="text-lg text-muted-foreground">*</span>
                            </div>
                        </div>
                        <p class="text-xs text-muted-foreground uppercase tracking-wider">{{ $t('pricing.daily.period') }}</p>
                        <p class="text-xs text-muted-foreground mt-1">{{ $t('pricing.vatNote') }}</p>
                    </div>

                    <div class="space-y-3 mb-8 mt-auto">
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-foreground font-medium">{{ $t('pricing.daily.frequency') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-foreground font-medium">{{ $t('pricing.features.setup') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-foreground font-medium">{{ $t('pricing.features.monitoring') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-foreground font-medium">{{ $t('pricing.features.support') }}</span>
                        </div>
                    </div>

                    <a
                        href="#contact"
                        class="block w-full text-center bg-accent hover:bg-accent/90 text-accent-foreground px-6 py-3 rounded-lg transition-all flex items-center justify-center gap-2 group uppercase text-sm tracking-wide font-medium shadow-md"
                    >
                        {{ $t('pricing.daily.cta') }}
                        <ArrowRightIcon class="size-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>

                <!-- Hourly Plan -->
                <div class="bg-white rounded-xl p-8 border border-border hover:shadow-lg transition-all flex flex-col">
                    <h3 class="text-xl mb-6 text-center">{{ $t('pricing.hourly.title') }}</h3>
                    <div class="text-center mb-8 flex-1 flex flex-col justify-center">
                        <div class="mb-3">
                            <div class="flex items-start justify-center gap-1">
                                <span class="text-5xl font-bold tracking-tight">{{ $t('pricing.hourly.price') }}</span>
                                <span class="text-5xl font-bold tracking-tight"> {{ $t('pricing.hourly.currency') }}</span>
                                <span class="text-lg text-muted-foreground">*</span>
                            </div>
                        </div>
                        <p class="text-xs text-muted-foreground uppercase tracking-wider">{{ $t('pricing.hourly.period') }}</p>
                        <p class="text-xs text-muted-foreground mt-1">{{ $t('pricing.vatNote') }}</p>
                    </div>

                    <div class="space-y-3 mb-8 mt-auto">
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.hourly.frequency') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.setup') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.monitoring') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.support') }}</span>
                        </div>
                    </div>

                    <a
                        href="#contact"
                        class="block w-full text-center border-2 border-border bg-white px-6 py-3 rounded-lg hover:bg-muted transition-all uppercase text-sm tracking-wide font-medium"
                    >
                        {{ $t('pricing.hourly.cta') }}
                    </a>
                </div>

                <!-- Webhook Plan -->
                <div class="bg-white rounded-xl p-8 border border-border hover:shadow-lg transition-all flex flex-col">
                    <h3 class="text-xl mb-6 text-center">{{ $t('pricing.webhook.title') }}</h3>
                    <div class="text-center mb-8 flex-1 flex flex-col justify-center">
                        <div class="mb-3">
                            <div class="flex items-center justify-center">
                                <span class="text-2xl font-bold tracking-tight">{{ $t('pricing.webhook.price') }}</span>
                            </div>
                        </div>
                        <p class="text-xs text-muted-foreground uppercase tracking-wider">{{ $t('pricing.webhook.period') }}</p>
                    </div>

                    <div class="space-y-3 mb-8 mt-auto">
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.webhook.frequency') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.setup') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.monitoring') }}</span>
                        </div>
                        <div class="flex items-start gap-3">
                            <CheckIcon class="size-5 text-primary shrink-0 mt-0.5" />
                            <span class="text-sm text-muted-foreground">{{ $t('pricing.features.support') }}</span>
                        </div>
                    </div>

                    <a
                        href="#contact"
                        class="block w-full text-center border-2 border-border bg-white px-6 py-3 rounded-lg hover:bg-muted transition-all uppercase text-sm tracking-wide font-medium"
                    >
                        {{ $t('pricing.webhook.cta') }}
                    </a>
                </div>
            </div>

            <!-- Custom Development Note -->
            <div class="text-center max-w-3xl mx-auto">
                <p class="text-xs text-muted-foreground">
                    {{ $t('pricing.customNote') }}
                </p>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { CheckIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';

export default defineComponent({
    name: 'PricingSection',
    components: {
        CheckIcon,
        ArrowRightIcon
    }
});
</script>
